import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Printer, AlertCircle, Loader2 } from 'lucide-react';

const PDFPrintDialog = ({
  showPrintDialog, setShowPrintDialog,
  displayName, numPages, pageNumber, maxPrintPages = 25,
  printPages, setPrintPages,
  isPrinting, printProgress, printError,
  handlePrint
}) => {
  return (
    <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
      <DialogContent 
        className="bg-slate-900 border-slate-700 text-white w-[90vw] max-w-[400px] overflow-hidden"
        style={{ zIndex: 99999 }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <div className="p-2 bg-teal-500/20 rounded-lg flex-shrink-0">
              <Printer className="h-5 w-5 text-teal-400" />
            </div>
            Impresion Controlada
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Selecciona las paginas a imprimir. Cada pagina incluira marca de agua de seguridad.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4 overflow-hidden">
          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 overflow-hidden">
            <p className="font-medium text-white truncate max-w-full" title={displayName}>{displayName}</p>
            <p className="text-sm text-slate-400 mt-1">
              Total: {numPages} paginas | Max: {maxPrintPages}
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-300 block mb-2">
              Paginas a imprimir
            </label>
            <Input
              placeholder={`Ej: 1-5, 10, ${pageNumber}`}
              value={printPages}
              onChange={(e) => setPrintPages(e.target.value)}
              disabled={isPrinting}
              className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 w-full"
            />
            <p className="text-xs text-slate-500 mt-2">
              Vacio = pagina actual ({pageNumber})
            </p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPrintPages(String(pageNumber))}
              disabled={isPrinting}
              className="bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              Actual
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setPrintPages(`1-${Math.min(maxPrintPages, numPages)}`)}
              disabled={isPrinting}
              className="bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              1-{Math.min(maxPrintPages, numPages)}
            </Button>
            {pageNumber > 1 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPrintPages(`${Math.max(1, pageNumber - 4)}-${Math.min(pageNumber + 5, numPages)}`)}
                disabled={isPrinting}
                className="bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                ±5 pags
              </Button>
            )}
          </div>
          
          {isPrinting && (
            <div className="bg-slate-800 p-4 rounded-xl border border-teal-500/30">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="h-5 w-5 text-teal-400 animate-spin" />
                <span className="text-white">Preparando paginas para impresion...</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all duration-300"
                  style={{ width: `${printProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">{printProgress}%</p>
            </div>
          )}
          
          {printError && (
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-300">{printError}</AlertDescription>
            </Alert>
          )}
          
          <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-xl">
            <p className="text-xs text-amber-300">
              <strong>Nota:</strong> Cada pagina impresa incluira marca de agua con datos del taller.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-2">
          <Button 
            variant="outline" 
            onClick={() => setShowPrintDialog(false)} 
            disabled={isPrinting}
            className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handlePrint} 
            disabled={isPrinting}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            {isPrinting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Preparando...
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                Imprimir
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PDFPrintDialog;
